import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo-data";
import { AlertTriangle, Phone, FileText, ExternalLink, CheckCircle2, ShieldAlert } from "lucide-react";

const STEPS = [
  {
    icon: AlertTriangle,
    color: "bg-orange-100 text-orange-600",
    title: "1. Ne pas paniquer — évaluer la situation",
    content: "Demandez-vous : ai-je déjà payé ? Ai-je fourni des informations bancaires ? La réponse à ces questions détermine l'urgence de vos démarches. Si vous n'avez pas encore payé, la décision est simple : abstenez-vous tant que vos doutes ne sont pas levés.",
  },
  {
    icon: FileText,
    color: "bg-blue-100 text-blue-600",
    title: "2. Rassembler les preuves immédiatement",
    content: "Conservez tout : captures d'écran du site (y compris les pages produits, les mentions légales si elles existent, les contacts), emails reçus, confirmation de commande, relevés bancaires. Ces documents seront essentiels pour tout recours.",
  },
  {
    icon: Phone,
    color: "bg-red-100 text-red-600",
    title: "3. Contacter votre banque sans attendre",
    content: "Si vous avez payé par carte bancaire, contactez votre banque le plus rapidement possible, idéalement dès que vous constatez le problème. Les délais de contestation varient selon les banques et les réseaux de carte.. Vous pouvez demander un « chargeback » — une procédure de contestation de paiement. C'est votre meilleur recours financier. Les virements bancaires sont beaucoup plus difficiles à récupérer.",
  },
  {
    icon: ShieldAlert,
    color: "bg-purple-100 text-purple-600",
    title: "4. Signaler le site sur SignalConso",
    content: "SignalConso (signal.conso.gouv.fr) est le service officiel de signalement des pratiques commerciales abusives. Votre signalement contribue à protéger d'autres consommateurs et peut déclencher une enquête par la Direction générale de la concurrence, de la consommation et de la répression des fraudes (DGCCRF).",
  },
  {
    icon: CheckCircle2,
    color: "bg-green-100 text-green-600",
    title: "5. Déposer plainte si nécessaire",
    content: "Si vous avez subi un préjudice financier, vous pouvez déposer une plainte auprès des forces de l'ordre (police, gendarmerie) ou via le portail de signalement des fraudes internet : internet-signalement.gouv.fr (géré par la Police nationale).",
  },
];

const RESOURCES = [
  { href: "https://signal.conso.gouv.fr", title: "SignalConso", desc: "Signalez une pratique commerciale abusive à la DGCCRF.", emoji: "🏛️" },
  { href: "https://www.service-public.fr/particuliers/vosdroits/F35949", title: "Service-Public.fr", desc: "Vos droits en cas de fraude liée à un achat sur Internet.", emoji: "📋" },
  { href: "https://www.cybermalveillance.gouv.fr", title: "Cybermalveillance.gouv.fr", desc: "Assistance aux victimes de cybermalveillance.", emoji: "🛡️" },
  { href: "https://internet-signalement.gouv.fr", title: "Internet-signalement.gouv.fr", desc: "Signaler un contenu illicite ou une fraude sur Internet.", emoji: "🚨" },
  { href: "https://www.cnil.fr", title: "CNIL", desc: "Pour les problèmes liés à vos données personnelles.", emoji: "🔒" },
];

export default function DoubtPage() {
  return (
    <div className="min-h-screen flex flex-col page-enter">
      <Seo
        title="Que faire en cas de doute ? — Guide pratique"
        description="Vous avez acheté sur un site douteux ? Voici les étapes à suivre : contacter votre banque, signaler sur SignalConso, déposer plainte. Ressources officielles françaises."
        path="/en-cas-de-doute"
        keywords="arnaque internet que faire, chargeback banque, SignalConso, fraude achat en ligne"
        jsonLd={breadcrumbJsonLd([
          { name: "Accueil", path: "/" },
          { name: "En cas de doute", path: "/en-cas-de-doute" },
        ])}
      />
      <Header />
      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-3xl">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#1E3A8A]">Guide pratique</span>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-heading text-slate-900 tracking-tight mt-3 mb-4">
              Que faire en cas de doute ?
            </h1>
            <p className="text-slate-600 text-base max-w-2xl mx-auto leading-relaxed">
              Vous avez un doute sur un site sur lequel vous avez commandé, ou vous envisagez un achat sur un site qui vous semble douteux ?
              Voici les étapes à suivre.
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-6 mb-12">
            {STEPS.map(({ icon: Icon, color, title, content }) => (
              <div key={title} className="card-base p-6" data-testid="doubt-step">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 mb-2 font-heading">{title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{content}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Prévention */}
          <div className="card-base p-6 mb-8">
            <h2 className="text-xl font-bold font-heading text-slate-800 mb-4">Avant d'acheter — les bons réflexes</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Vérifiez le site avec Fiable ou Pas avant toute transaction.",
                "Ne cédez pas aux offres trop belles pour être vraies.",
                "Privilégiez les paiements par carte ou PayPal.",
                "Recherchez des avis indépendants sur Trustpilot.",
                "Vérifiez les mentions légales et les CGV.",
                "Conservez la confirmation de commande.",
              ].map(t => (
                <div key={t} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                  {t}
                </div>
              ))}
            </div>
          </div>

          {/* Resources */}
          <div className="mb-8">
            <h2 className="text-xl font-bold font-heading text-slate-800 mb-5">Ressources officielles en France</h2>
            <div className="space-y-3">
              {RESOURCES.map(({ href, title, desc, emoji }) => (
                <a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 card-base hover:shadow-md transition-shadow"
                  data-testid={`resource-${title}`}
                >
                  <span className="text-2xl w-10 text-center flex-shrink-0">{emoji}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{title}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-slate-400 flex-shrink-0" />
                </a>
              ))}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 text-sm text-slate-700">
            <p className="font-semibold mb-1">Rappel important</p>
            <p>Les informations de cette page sont fournies à titre indicatif et ne constituent pas un conseil juridique. Chaque situation est différente. En cas de doute complexe, consultez une association de consommateurs (UFC-Que Choisir, CLCV) ou un professionnel du droit.</p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
